import { ApiError, type AdminApi } from "@/api";
import { TraceView } from "@/components/llm-trace";
import { fromReplaySnapshot } from "@/utils/trace/normalize";
import { mapValue, stringValue } from "@/utils/coerce";
import { useMemo } from "react";
import type { JsonObject } from "@/types/issuePipeline";

export function pipelineError(error: unknown) {
  return error instanceof ApiError && error.code
    ? `${error.code}: ${error.message}`
    : error instanceof Error
      ? error.message
      : String(error);
}

export function parseObject(value: string, label: string): JsonObject {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    throw new Error(`${label}: 올바른 JSON을 입력해 주세요.`);
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
    throw new Error(`${label}: JSON 객체가 필요합니다.`);
  return parsed as JsonObject;
}

export function conversationHref(
  environment: string,
  session: string,
  message?: string | null,
  store?: "sams",
) {
  const query = new URLSearchParams({
    env: environment === "prod" ? "prod" : "dev",
    session,
  });
  if (message) query.set("message", message);
  if (store) query.set("store", store);
  return `/conversations?${query}`;
}

export function JsonDetails({
  title,
  value,
}: {
  title: string;
  value: unknown;
}) {
  return (
    <details className="rounded-lg border border-border p-3">
      <summary className="cursor-pointer text-sm font-medium">{title}</summary>
      <pre className="mt-2 max-h-96 overflow-auto whitespace-pre-wrap break-all text-xs">
        {JSON.stringify(value, null, 2)}
      </pre>
    </details>
  );
}

export function ReplayEvidence({
  api,
  item,
}: {
  api: AdminApi;
  item: JsonObject;
}) {
  const trace = useMemo(() => fromReplaySnapshot(item), [item]);
  const answer = mapValue(item.assistant_message);
  const session = stringValue(item.replay_session_id);
  return (
    <div className="grid gap-3">
      {typeof answer.content === "string" && (
        <p className="rounded border border-border p-3 text-sm whitespace-pre-wrap">
          {answer.content}
        </p>
      )}
      {session && (
        <a
          className="text-sm text-primary"
          href={conversationHref(
            "dev",
            session,
            stringValue(answer.chat_message_id),
            api.hasSamsRuntimeHost ? "sams" : undefined,
          )}
        >
          보존된 테스트 conversation ↗
        </a>
      )}
      <TraceView trace={trace} ioContext={{ api, environment: "dev" }} />
      <JsonDetails title="보존된 실행 결과" value={item} />
    </div>
  );
}

export const inputClass =
  "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm";

export function Field({
  label,
  value,
  onChange,
  multiline = false,
  rows = 3,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <label className="grid gap-1 text-xs text-muted-foreground">
      {label}
      {multiline ? (
        <textarea
          className={inputClass}
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      ) : (
        <input
          className={inputClass}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      )}
    </label>
  );
}
